// socketService - Socket.io client service for real-time collaboration
// Implements US-4.1: WebSocket Infrastructure and Presence System
// Manages WebSocket connection lifecycle and event handling

import { io, Socket } from 'socket.io-client';
import { ClientToServerEvents, ServerToClientEvents } from '../types/socket';
import { getToken, getViewerToken } from '../utils/tokenStorage';

// TYPE-SAFE Socket.io client with event interfaces
type TypedSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

/**
 * Socket.io client instance
 * Initialized on first connection
 */
let socket: TypedSocket | null = null;

/**
 * Flag to prevent concurrent socket creation
 * React Strict Mode can cause multiple simultaneous connectSocket() calls
 */
let isCreating = false;

/**
 * CONNECT to Socket.io server
 * Authenticates using JWT token from storage (user or viewer)
 * Returns connected socket instance
 *
 * @returns Socket.io client instance
 */
export function connectSocket(): TypedSocket {
  // RETURN existing socket instance if already created (singleton pattern)
  // NOTE: Return even if not yet connected - connection is async and takes time
  if (socket) {
    return socket;
  }

  // PREVENT concurrent socket creation (React Strict Mode protection)
  if (isCreating) {
    throw new Error('Socket creation in progress, please retry');
  }

  // SET creation flag to prevent concurrent calls
  isCreating = true;

  try {
    // GET token from storage (try user token first, then viewer token)
    const userToken = getToken();
    const viewerToken = getViewerToken();
    const token = userToken || viewerToken;

    if (!token) {
      throw new Error('No authentication token found. Please log in again.');
    }

    // GET server URL from environment variable
    const serverUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';

    // CREATE Socket.io client with authentication
    socket = io(serverUrl, {
    auth: {
      token,
    },
    transports: ['websocket', 'polling'], // Try WebSocket first, fallback to polling
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    reconnectionAttempts: 5,
  });

  // HANDLE connection events
  socket.on('connect', () => {
    console.log('✓ Socket.io connected:', socket?.id);
  });

  socket.on('disconnect', (reason) => {
    console.log('✗ Socket.io disconnected:', reason);
  });

  socket.on('connect_error', (error) => {
    console.error('✗ Socket.io connection error:', error.message);
  });

  socket.on('error', (error) => {
    console.error('✗ Socket.io error:', error.message);
  });

    return socket;
  } finally {
    // RESET creation flag
    isCreating = false;
  }
}

/**
 * DISCONNECT from Socket.io server
 * Closes the connection and cleans up resources
 */
export function disconnectSocket(): void {
  if (socket) {
    // CLEANUP: Remove listener registration marker and handlers ref
    // This allows fresh registration if user logs back in
    const socketAny = socket as any;
    if (socketAny._arbiterListenersRegistered) {
      delete socketAny._arbiterListenersRegistered;
    }
    if (socketAny._arbiterHandlersRef) {
      delete socketAny._arbiterHandlersRef;
    }

    socket.disconnect();
    socket = null;
  }
}

/**
 * GET current Socket.io client instance
 * Returns null if not connected
 *
 * @returns Socket.io client instance or null
 */
export function getSocket(): TypedSocket | null {
  return socket;
}

/**
 * JOIN workspace room
 * Emits workspace:join event to server
 *
 * @param workspaceId - Workspace ID to join
 */
export function joinWorkspace(workspaceId: string): void {
  // CHECK if socket exists and has an ID (more reliable than checking .connected)
  // socket.id is set immediately when connection happens, even if .connected is still false
  if (!socket || !socket.id) {
    throw new Error('Socket not connected. Call connectSocket() first.');
  }

  socket.emit('workspace:join', { workspaceId });
}

/**
 * CHECK if socket is currently connected
 *
 * @returns True if connected, false otherwise
 */
export function isConnected(): boolean {
  return socket?.connected ?? false;
}
